import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Shield, ExternalLink, ArrowRight, Calendar, Eye } from "lucide-react";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { ToolCallCard } from "../components/ToolCallCard";
import { getJSON } from "../lib/api";

/**
 * /share/:shareId — public, indexable Q&A page.
 *
 * Loads the share from GET /api/share/{shareId} and renders:
 *   - The user's original prompt as H1
 *   - Each tool result via <ToolCallCard /> (in success state)
 *   - The final AI answer
 *   - A "Try the co-pilot" CTA to the homepage chat
 *   - A "Related tools" section for SEO/GEO interlinking
 *
 * SEO:
 *   - <title> = prompt (truncated)
 *   - <meta description> = answer (truncated to 155 chars)
 *   - <link rel=canonical> = SITE_DOMAIN/share/{id}
 *   - JSON-LD QAPage so Google can surface the answer in rich results / SGE
 *   - index, follow (default in SEO component)
 */
export default function SharedAnswer() {
    const { shareId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getJSON(`/share/${encodeURIComponent(shareId || "")}`)
            .then((r) => {
                if (!cancelled) {
                    setData(r);
                    setLoading(false);
                }
            })
            .catch((e) => {
                if (cancelled) return;
                const status = e?.response?.status;
                setError(status === 404 ? "not_found" : "load_error");
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [shareId]);

    if (loading) {
        return (
            <div
                className="min-h-screen flex items-center justify-center text-text-muted"
                data-testid="share-loading"
            >
                <Loader2 className="w-5 h-5 animate-spin text-accent-cyan mr-2" />
                Loading shared answer…
            </div>
        );
    }

    if (error === "not_found") {
        return (
            <main
                className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 md:px-10 text-center"
                data-testid="share-not-found"
            >
                <SEO
                    title="Shared answer not found"
                    description="This shared answer is no longer available."
                    noindex
                />
                <h1 className="font-display text-4xl text-text-primary">
                    This shared answer is no longer available.
                </h1>
                <p className="mt-4 text-text-muted">
                    The link may be invalid, or the author may have unpublished it.
                </p>
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 mt-8 px-4 py-2 rounded-md bg-accent-cyan text-canvas font-medium hover:shadow-glow-cyan transition-shadow"
                    data-testid="share-back-home"
                >
                    Try the AI co-pilot <ArrowRight className="w-4 h-4" />
                </Link>
            </main>
        );
    }

    if (error || !data) {
        return (
            <main className="pt-24 pb-20 mx-auto max-w-3xl px-5 md:px-10 text-center">
                <SEO title="Could not load shared answer" noindex />
                <p className="text-accent-red">Could not load this shared answer.</p>
            </main>
        );
    }

    const {
        share_id,
        user_prompt,
        assistant_reply,
        tool_calls = [],
        view_count = 0,
        created_at,
        creator_label,
    } = data;

    // Build SEO description: prefer the AI answer, fall back to the prompt.
    const seoTitle = user_prompt?.slice(0, 80) || "Cybersecurity Forefront AI answer";
    const seoDescription = (assistant_reply || user_prompt || "")
        .replace(/\s+/g, " ")
        .slice(0, 155);
    const canonicalUrl = `${SITE_DOMAIN}/share/${share_id}`;

    // Schema.org QAPage so Google / Bing / SGE / Perplexity can lift this
    // straight into rich results & AI overviews. We keep it tight; the
    // assistant_reply is the acceptedAnswer.text.
    const qaSchema = {
        "@context": "https://schema.org",
        "@type": "QAPage",
        mainEntity: {
            "@type": "Question",
            name: user_prompt,
            text: user_prompt,
            answerCount: 1,
            acceptedAnswer: {
                "@type": "Answer",
                text: (assistant_reply || "").slice(0, 1500),
                url: canonicalUrl,
                author: { "@type": "Organization", name: "Cybersecurity Forefront" },
            },
            author: creator_label
                ? { "@type": "Person", name: creator_label }
                : { "@type": "Organization", name: "Cybersecurity Forefront" },
            datePublished: created_at,
        },
    };

    const createdLabel = created_at
        ? new Date(created_at).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
          })
        : null;

    return (
        <main
            className="pt-24 md:pt-28 pb-20 mx-auto max-w-3xl px-5 md:px-10"
            data-testid="shared-answer-page"
        >
            <SEO
                title={seoTitle}
                description={seoDescription || undefined}
                canonicalUrl={canonicalUrl}
                ogType="article"
                schema={qaSchema}
            />

            {/* Breadcrumb-ish back link */}
            <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-[12px] font-mono uppercase tracking-wider text-text-dim hover:text-accent-cyan transition-colors"
                data-testid="share-back-to-copilot"
            >
                ← Cybersecurity Forefront AI co-pilot
            </Link>

            {/* H1 = the question */}
            <h1
                className="mt-4 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight text-text-primary"
                data-testid="share-prompt"
            >
                {user_prompt}
            </h1>

            {/* Metadata strip */}
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] font-mono text-text-dim">
                <span className="inline-flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5 text-accent-cyan" />
                    Answered by the AI co-pilot
                </span>
                {creator_label && <span>· shared by {creator_label}</span>}
                {createdLabel && (
                    <span className="inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {createdLabel}
                    </span>
                )}
                <span
                    className="inline-flex items-center gap-1"
                    data-testid="share-view-count"
                >
                    <Eye className="w-3 h-3" />
                    {view_count} view{view_count === 1 ? "" : "s"}
                </span>
            </div>

            {/* Tool result cards */}
            {tool_calls.length > 0 && (
                <section
                    className="mt-8 space-y-3"
                    data-testid="shared-answer-tool-section"
                >
                    <h2 className="font-display text-sm font-medium uppercase tracking-wider text-text-dim">
                        Tools the co-pilot ran
                    </h2>
                    {tool_calls.map((tc) => (
                        <ToolCallCard
                            key={tc.tool_call_id}
                            name={tc.name}
                            args={tc.args || {}}
                            status={tc.success ? "success" : "error"}
                            result={tc.result}
                            error={tc.error}
                            errorCode={tc.error_code}
                            durationMs={tc.duration_ms}
                        />
                    ))}
                </section>
            )}

            {/* AI answer */}
            {assistant_reply && (
                <section className="mt-8" data-testid="shared-answer-reply">
                    <h2 className="font-display text-sm font-medium uppercase tracking-wider text-text-dim">
                        Answer
                    </h2>
                    <p className="mt-2 text-[15px] leading-relaxed text-text-primary whitespace-pre-wrap">
                        {assistant_reply}
                    </p>
                </section>
            )}

            {/* CTA — drive traffic back to the live co-pilot */}
            <section
                className="mt-10 rounded-2xl border border-accent-cyan/20 bg-accent-cyan/[0.04] p-6"
                data-testid="shared-answer-cta"
            >
                <h2 className="font-display text-xl text-text-primary">
                    Want to ask your own questions?
                </h2>
                <p className="mt-2 text-text-muted text-[14px] leading-relaxed">
                    The Cybersecurity Forefront AI co-pilot answers subnet, DNS, and CVE
                    questions instantly — plus (for Pro accounts) live queries against
                    your own IPAM and CVE alerts. Free to try, no signup required.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas font-medium hover:shadow-glow-cyan transition-shadow"
                        data-testid="shared-cta-copilot"
                    >
                        Try the AI co-pilot <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                        to="/tools"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-white/10 text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors"
                        data-testid="shared-cta-tools"
                    >
                        Browse free tools
                    </Link>
                </div>
            </section>

            {/* Related tools — interlinking for SEO/GEO */}
            <aside className="mt-12" data-testid="shared-answer-related-tools">
                <h2 className="font-display text-sm font-medium uppercase tracking-wider text-text-dim">
                    Related free tools
                </h2>
                <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {[
                        ["Subnet calculator", "/tools/subnet-calculator"],
                        ["DNS health check", "/tools/dns-health"],
                        ["CVE lookup", "/tools/cve-lookup"],
                        ["TLS inspector", "/tools/tls-inspector"],
                        ["Security headers", "/tools/security-headers"],
                        ["NIS2 assessment", "/tools/nis2-assessment"],
                    ].map(([label, href]) => (
                        <li key={href}>
                            <Link
                                to={href}
                                className="block rounded-md border border-white/[0.06] px-3 py-2 text-[13px] text-text-primary hover:border-accent-cyan/40 hover:text-accent-cyan transition-colors"
                            >
                                <span className="font-mono">{label}</span>
                                <span className="text-text-dim ml-2 text-[11.5px]">{href}</span>
                            </Link>
                        </li>
                    ))}
                </ul>
            </aside>

            {/* Footer attribution */}
            <footer className="mt-12 pt-6 border-t border-white/[0.05] text-[12px] text-text-dim">
                <p>
                    This answer was generated by{" "}
                    <Link
                        to="/"
                        className="text-accent-cyan hover:underline inline-flex items-center gap-1"
                    >
                        Cybersecurity Forefront <ExternalLink className="w-3 h-3" />
                    </Link>
                    , the chat-first network operations co-pilot for MSPs and security
                    engineers.
                </p>
            </footer>
        </main>
    );
}
